
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import Toast from '@/utils/toast';
import IconButton from '@/components/shared/suites/IconButton.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace = 'orders';

@Component<ModifyFee>({
	components: {
		IconButton,
		TextButton,
		TextField,
		ValidationObserver,
		ValidationProvider
	}
})
export default class ModifyFee extends Vue {
	@Prop({ type: String, required: true }) private type!: FeeType;
	@Prop({ type: String, required: true }) private location!: string;
	@Prop({ type: String, required: false, default: '' }) private label!: string;
	@Prop({ type: Boolean, required: true }) private isModifyingFee!: boolean;
	@Action('getDeliveryFees', { namespace }) private getDeliveryFees!: (payload: { costs: Costs, checkout: Checkout }) => Promise<CostsDeliveryCharge>;
	@Action('setModifiedFees', { namespace }) private setModifiedFees!: (payload: { type: FeeType, updatedFee: CostsServiceCharge | CostsDeliveryCharge }) => Promise<void>;
	@Getter('getCosts', { namespace }) private costs!: Costs;
	@Getter('getCheckoutInfo', { namespace }) private checkout!: Checkout;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('getPriceAffixes', { namespace: 'auth' }) private priceAffixes!: PriceAffixes;

	$refs!: { observer: Validate };

	private updatedFee: CostsServiceCharge | CostsDeliveryCharge = { amount: 0, percentageValue: 0, type: 'dollar' };
	private editedAmount: string | null = null;
	private defaultFee: string | null = null;
	private applyingFee: boolean = false;
	private resettingFee: boolean = false;

	private get idBase(): string {
		return this.type === 'serviceCharge' ? 'service' : 'delivery';
	}

	/**
	 * A flag for if the fee has been modified when applied.
	 * A modified fee in the costs state keeps the modified flag until it's explicitly reset.
	 * An unmodified fee only becomes modified when the entered amount differs.
	 *
	 * @return {boolean}
	 */
	private get feeModified(): boolean {
		if (this.defaultFee !== null) {
			return this.editedAmount !== null && +this.editedAmount !== +this.defaultFee;
		}

		return this.feeModifiedInStore || (this.editedAmount !== null && +this.editedAmount !== +this.currentFee);
	}

	private get feeModifiedInStore(): boolean {
		return !!this.costs[this.type].feeModified;
	}

	/**
	 * The current fee amount from the costs state, formatted to two decimals.
	 *
	 * @return {string}
	 */
	private get currentFee(): string {
		return (+(this.costs[this.type].amount || 0)).toFixed(2);
	}

	/**
	 * The value bound to the input, shows the operator's edit while modifying.
	 * Otherwise show the fee from the costs state so it stays in sync when the fee is recalculated (e.g. after an address change).
	 *
	 * @return {string}
	 */
	private get displayAmount(): string {
		return this.isModifyingFee && this.editedAmount !== null ? this.editedAmount : this.currentFee;
	}

	/**
	 * Set displayAmount to the operator's edit.
	 *
	 * @param {string} value
	 * @return {void}
	 */
	private set displayAmount(value: string) {
		this.editedAmount = value;
	}

	private created(): void {
		this.updatedFee = {
			...this.costs[this.type],
			type: 'dollar',
			percentageValue: 0
		};
	}

	/**
	 * On blur, round the entered amount to two decimals.
	 * Empty/invalid input is skipped so it isn't turned into a valid "0.00" and validation can flag it.
	 *
	 * @return {void}
	 */
	private formatDisplayAmount(): void {
		if (this.editedAmount === null || this.editedAmount === '' || isNaN(+this.editedAmount)) {
			return;
		}
		this.editedAmount = (+this.editedAmount).toFixed(2);
	}

	/**
	 * Persist the modified fee amount and close the editor.
	 *
	 * @return {Promise<void>}
	 */
	private async applyFee(): Promise<void> {
		// Nothing to persist if neither the amount nor the modified flag has changed
		if (this.editedAmount === null || (+this.editedAmount === +this.currentFee && this.feeModified === this.feeModifiedInStore)) {
			this.closeEditor();
			return;
		}
		if (await this.$refs.observer.validate()) {
			try {
				this.applyingFee = true;
				const updatedFee: CostsServiceCharge | CostsDeliveryCharge = { ...this.updatedFee, amount: +(this.displayAmount || 0), feeModified: this.feeModified };
				// Modified fees are always flat dollar amounts, regardless of the configured fee type
				if (this.feeModified) {
					updatedFee.type = 'dollar';
					updatedFee.percentageValue = 0;
				}
				await this.setModifiedFees({ type: this.type, updatedFee });
				this.closeEditor();
			}
			catch (error) {
				Toast.error(this.$t('orders.cart.pricing.apply_charge_error', { feeType: this.$t(`orders.cart.pricing.${this.idBase}_fee_name`) }));
			}
			finally {
				this.applyingFee = false;
			}
		}
	}

	/**
	 * Restore the input to the location's default fee.
	 * Fee will only be persisted once the change is applied.
	 *
	 * @return {Promise<void>}
	 */
	private async resetFee(): Promise<void> {
		try {
			// Only show loading spinner if default fee needs to be fetched from Remy
			this.resettingFee = this.type === 'deliveryCharge' && this.restaurant.delivery?.delivery_fee_type === 'custom';
			this.updatedFee = await this.calculateDefaultFee();
			this.defaultFee = (+this.updatedFee.amount).toFixed(2);
			this.editedAmount = this.defaultFee;
		}
		catch (error) {
			Toast.error(this.$t('orders.cart.pricing.reset_charge_error', { feeType: this.$t(`orders.cart.pricing.${this.idBase}_fee_name`) }));
		}
		finally {
			this.resettingFee = false;
		}
	}

	/**
	 * Clear the editing state and close the editor.
	 * The state must be cleared explicitly because in the checkout location the component stays mounted between edits.
	 *
	 * @return {void}
	 */
	private closeEditor(): void {
		this.editedAmount = null;
		this.defaultFee = null;
		this.$emit('close');
	}

	/**
	 * Calculate the default fee from the location's configuration.
	 * If delivery fee uses radius based fees, then we need to get the default fee from Remy,
	 * since it may have changed if the address moved to a different delivery zone.
	 * Otherwise flat or percentage based fees can be calculated locally.
	 * NOTE: If fee calculation changes on Remy this needs to be updated as well, or vice-versa
	 *
	 * @return {Promise<CostsServiceCharge | CostsDeliveryCharge>}
	 */
	private async calculateDefaultFee(): Promise<CostsServiceCharge | CostsDeliveryCharge> {
		if (this.type === 'deliveryCharge' && this.restaurant.delivery?.delivery_fee_type === 'custom') {
			return await this.getDeliveryFees({ costs: { ...this.costs, deliveryCharge: { ...this.costs.deliveryCharge, feeModified: false } }, checkout: this.checkout });
		}
		else {
			const config: string | undefined = this.type === 'serviceCharge' ? this.restaurant.service_charge : this.restaurant.delivery?.delivery_charge;
			// With no configured charge there is no default fee, which set-costs will calculate as $0
			if (!config) {
				return { ...this.costs[this.type], type: 'dollar', percentageValue: 0, amount: 0 };
			}
			const feeValue: number = +config.slice(0, -1);
			const feeType: AmountType = config.slice(-1) === '$' ? 'dollar' : 'percentage';
			const amount: number = feeType === 'dollar' ? feeValue : this.costs.subtotal * (feeValue / 100);

			return { ...this.costs[this.type], type: feeType, percentageValue: feeType === 'percentage' ? feeValue : 0, amount };
		}
	}
}
